/**
 * PayAppEditorModal — 5-step pay app editor (PA #2+ and edit existing).
 */
import { useEffect, useRef, useState, FormEvent, type CSSProperties } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createPayAppDraft, fetchPayAppDetail, fetchProjectPayApps, fetchProject, createRetainageRelease, dismissPayAppRetentionNotice, updatePayAppLines } from '../../../api/projects'
import { submitPayApp, getPayAppPdfUrl, emailPayApp, getToken } from '../../../api/payApps'
import { loadSettings } from '../../../api/settings'
import { PayAppG703Grid } from '../../projects-new/steps/Step4PayApp/Sub1G703Billing'
import ChangeOrdersStep, { type ChangeOrdersStepHandle } from './ChangeOrdersStep'
import AttachmentsStep from './AttachmentsStep'
import ModalPortal, { modalBoxStyle } from '../../../components/ModalPortal'
import RichTextNotes from '../../../components/RichTextNotes'
import { RETAINAGE_WARN_THRESHOLD, applyDefaultRetainageToBelow, toEditableLine, buildLinesSavePayload, anyLineExceeds100, overbillLineMessage } from '../../../lib/g703GridLogic'
import { editableLinesFromDetail, persistThenEmailPayApp, firstOverbilledLine } from '../../../lib/payAppEmailFlow'
import { isPayAppLockedForLineEdits } from '../../../lib/coLabel'
import { loadG703Draft, clearG703Draft } from '../../../lib/payAppGridDrafts'
import type { G703DraftSlice } from '../../../lib/g703GridLogic'
import {
  countLinesBelowDefault,
  retentionNoticeMessage,
  isRetentionNoticeDismissed,
  resolveRetentionDefault,
} from '../../../lib/retentionDefaultNotice'
import ContractBreakdown from '../../../components/billing/ContractBreakdown'
import { billingProgressForProject } from '../../../lib/projectMoneyDisplay'
import {
  PAYAPP_EDITOR_STEP_LABELS,
  PAYAPP_EDITOR_TOTAL_STEPS,
  parseEditorStepFromUrl,
} from '../../../lib/payAppEditorSteps'
import { track } from '../../../lib/track'

export interface PayAppEditorModalProps {
  projectId: number
  payAppId: number | null
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  /** When true, renders as in-shell page content (no backdrop). */
  embedded?: boolean
}

const STEP_LABELS = PAYAPP_EDITOR_STEP_LABELS
const TOTAL_STEPS = PAYAPP_EDITOR_TOTAL_STEPS

const lbl: CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 }
const fld: CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13 }

function refreshDetail(qc: ReturnType<typeof useQueryClient>, activeId: number) {
  qc.invalidateQueries({ queryKey: ['pay-app-detail', activeId] })
}

function fmtMoney(raw: string | number | null | undefined): string {
  const n = typeof raw === 'number' ? raw : parseFloat(raw ?? '0')
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function sumActiveChangeOrders(
  orders: { amount: string; status: string }[] | undefined,
): number {
  return (orders ?? [])
    .filter(co => co.status !== 'void' && co.status !== 'voided')
    .reduce((s, co) => s + (parseFloat(co.amount) || 0), 0)
}

export default function PayAppEditorModal({
  projectId, payAppId, isOpen, onClose, onSaved, embedded = false,
}: PayAppEditorModalProps) {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [activeId, setActiveId] = useState<number | null>(payAppId)
  const [periodLabel, setPeriodLabel] = useState('')
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd] = useState('')
  const [poNumber, setPoNumber] = useState('')
  const [specialNotes, setSpecialNotes] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [emailTo, setEmailTo] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailModalMessage, setEmailModalMessage] = useState<string | null>(null)
  const [waiverGenerated, setWaiverGenerated] = useState(false)
  const [payAppSubmitted, setPayAppSubmitted] = useState(false)
  const [g703Draft, setG703Draft] = useState<G703DraftSlice[] | undefined>(undefined)
  const [retNoticeDismissed, setRetNoticeDismissed] = useState(false)
  const [highlightRetCells, setHighlightRetCells] = useState(false)
  const [gridValid, setGridValid] = useState(true)
  const [billRetentionBusy, setBillRetentionBusy] = useState(false)
  const hydratedForId = useRef<number | null>(null)
  const changeOrdersRef = useRef<ChangeOrdersStepHandle>(null)
  const openSessionRef = useRef('')

  const visible = isOpen || embedded
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()

  const { data: detail } = useQuery({
    queryKey: ['pay-app-detail', activeId],
    queryFn: () => fetchPayAppDetail(activeId!),
    enabled: visible && activeId != null,
  })

  const { data: existingPayApps = [] } = useQuery({
    queryKey: ['project-payapps', projectId],
    queryFn: () => fetchProjectPayApps(projectId),
    enabled: visible && payAppId === null,
    staleTime: 30_000,
  })

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => fetchProject(projectId),
    enabled: visible && Number.isFinite(projectId),
    staleTime: 30_000,
  })

  const { data: companySettings } = useQuery({
    queryKey: ['company-settings'],
    queryFn: loadSettings,
    enabled: visible,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (payAppId !== null) return
    if (existingPayApps.length === 0) return
    const nextNum = existingPayApps.length + 1
    setPeriodLabel(prev => prev ? prev : `Pay App #${nextNum}`)
  }, [existingPayApps, payAppId])

  useEffect(() => {
    if (payAppId !== null) return
    if (existingPayApps.length === 0) return
    const last = existingPayApps[existingPayApps.length - 1]
    if (last.period_end && !periodStart) {
      const lastEnd = new Date(last.period_end)
      const nextStart = new Date(lastEnd)
      nextStart.setDate(nextStart.getDate() + 1)
      const nextEnd = new Date(nextStart)
      nextEnd.setMonth(nextEnd.getMonth() + 1)
      nextEnd.setDate(0)
      setPeriodStart(nextStart.toISOString().slice(0, 10))
      setPeriodEnd(nextEnd.toISOString().slice(0, 10))
    }
  }, [existingPayApps, payAppId])

  useEffect(() => {
    if (payAppId !== null) return
    if (existingPayApps.length === 0) return
    const last = existingPayApps[existingPayApps.length - 1]
    const rollPo = last.po_number
    if (rollPo) setPoNumber(prev => prev ? prev : rollPo)
  }, [existingPayApps, payAppId])

  useEffect(() => {
    if (payAppId !== null) return
    if (existingPayApps.length === 0) return
    const last = existingPayApps[existingPayApps.length - 1]
    const rollNotes = last.special_notes
    if (rollNotes) setSpecialNotes(prev => prev ? prev : rollNotes)
  }, [existingPayApps, payAppId])

  useEffect(() => {
    if (!visible) {
      openSessionRef.current = ''
      hydratedForId.current = null
      return
    }
    const session = `${payAppId ?? 'new'}:${searchParams.get('step') ?? '1'}`
    if (openSessionRef.current === session) return
    openSessionRef.current = session

    const initialStep = payAppId != null ? parseEditorStepFromUrl(searchParams.get('step')) : 1
    setStep(initialStep)
    setActiveId(payAppId)
    hydratedForId.current = null
    setErr(null)
    setEmailSent(false)
    setShowEmailModal(false)
    setEmailModalMessage(null)
    setWaiverGenerated(false)
    setPayAppSubmitted(false)
    setG703Draft(undefined)
  }, [visible, payAppId, searchParams])

  useEffect(() => {
    setRetNoticeDismissed(isRetentionNoticeDismissed(detail))
  }, [detail])

  useEffect(() => {
    if (!detail) return
    if (hydratedForId.current === detail.id) return
    hydratedForId.current = detail.id
    setPeriodLabel(detail.period_label ?? '')
    setPeriodStart(detail.period_start?.slice(0, 10) ?? '')
    setPeriodEnd(detail.period_end?.slice(0, 10) ?? '')
    setPoNumber(detail.po_number ?? '')
    setSpecialNotes(detail.special_notes ?? '')
  }, [detail])

  useEffect(() => {
    if (detail && !emailTo) {
      setEmailTo(detail.owner_email ?? '')
    }
  }, [detail, emailTo])

  useEffect(() => {
    if (activeId == null) return
    if (g703Draft !== undefined) return
    const stored = loadG703Draft(activeId)
    if (stored?.length) setG703Draft(stored)
  }, [activeId, g703Draft])

  if (!visible) return null

  async function handleStep1(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    try {
      let id = activeId
      if (id == null) {
        const created = await createPayAppDraft(projectId, {
          period_label: periodLabel || null,
          period_start: periodStart || null,
          period_end: periodEnd || null,
          po_number: poNumber || null,
          special_notes: specialNotes || null,
        })
        id = created.id
        setActiveId(id)
        hydratedForId.current = id
        track('pay_app_created', { project_id: projectId, pay_app_id: id })
      } else {
        void submitPayApp(id, {
          period_label: periodLabel || undefined,
          period_start: periodStart || undefined,
          period_end: periodEnd || undefined,
          po_number: poNumber || undefined,
          special_notes: specialNotes || undefined,
          status: 'draft',
        }).catch(ex => {
          setErr(ex instanceof Error ? ex.message : 'Save failed')
        })
      }
      setStep(2)
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  async function handleSubmit() {
    if (!activeId) return
    const bad = firstOverbilledLine(editableLinesFromDetail(detail?.lines, g703Draft))
    if (bad) {
      setErr(overbillLineMessage(bad))
      return
    }
    setErr(null)
    setBusy(true)
    try {
      await submitPayApp(activeId, { status: 'submitted' })
      setPayAppSubmitted(true)
      setWaiverGenerated(true)
      await qc.invalidateQueries({ queryKey: ['project-payapps', projectId] })
      refreshDetail(qc, activeId)
      onSaved()
      setShowEmailModal(true)
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Submit failed')
    } finally {
      setBusy(false)
    }
  }

  async function sendPayAppEmail() {
    if (!activeId || !emailTo.trim()) return
    const lines = editableLinesFromDetail(detail?.lines, g703Draft)
    const status = detail?.status || (payAppSubmitted ? 'submitted' : 'draft')
    await persistThenEmailPayApp({
      payAppId: activeId,
      emailTo: emailTo.trim(),
      lines,
      status,
      persist: (id, body) => updatePayAppLines(id, body),
      email: (id, data) => emailPayApp(id, {
        to_email: data.to_email,
        ...(isPayAppLockedForLineEdits(status) ? {} : { lines: data.lines }),
      }),
    })
    setEmailSent(true)
    track('pay_app_sent', { pay_app_id: activeId, project_id: projectId })
  }

  async function handleEmail() {
    if (!activeId || !emailTo.trim()) return
    setErr(null)
    setBusy(true)
    try {
      await sendPayAppEmail()
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Email failed')
    } finally {
      setBusy(false)
    }
  }

  async function handleStep2Next() {
    const ok = await changeOrdersRef.current?.prepareAdvance()
    if (ok !== false) setStep(3)
  }

  async function handleEmailFromModal() {
    if (!activeId || !emailTo.trim()) return
    setErr(null)
    setEmailModalMessage(null)
    setBusy(true)
    try {
      const sentTo = emailTo.trim()
      await sendPayAppEmail()
      setEmailModalMessage(`✓ Email sent to ${sentTo}`)
      window.setTimeout(() => {
        setShowEmailModal(false)
        setEmailModalMessage(null)
      }, 1500)
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Email failed')
    } finally {
      setBusy(false)
    }
  }

  const boxStyle = embedded
    ? { width: '100%', maxWidth: '100%', padding: '24px' }
    : modalBoxStyle

  const changeOrderTotal = sumActiveChangeOrders(detail?.change_orders)
  const detailMatches = detail?.id === activeId
  const serverAmountDue = detailMatches && detail.amount_due != null && detail.amount_due !== ''
    ? parseFloat(detail.amount_due)
    : null
  const serverRetention = detailMatches && detail.retention_held != null && detail.retention_held !== ''
    ? parseFloat(detail.retention_held)
    : null
  const serverRetentionThisPeriod = detailMatches
      && detail.retention_this_period != null && detail.retention_this_period !== ''
    ? parseFloat(detail.retention_this_period)
    : null
  // Single source of truth: the server-persisted snapshot (same numbers as the PDF).
  const step5AmountDue = serverAmountDue
  const step5Retainage = serverRetentionThisPeriod ?? serverRetention
  const showChangeOrderCaption = changeOrderTotal > 0
  const isSubmitted = payAppSubmitted || isPayAppLockedForLineEdits(detail?.status)
  const projectRetention = parseFloat(project?.retention_held ?? '0') || 0
  const billingProgress = project ? billingProgressForProject(project) : null

  async function handleBillRetention() {
    setBillRetentionBusy(true)
    setErr(null)
    try {
      const result = await createRetainageRelease(projectId)
      await qc.invalidateQueries({ queryKey: ['project-payapps', projectId] })
      if (result.pay_app_id) {
        navigate(`/projects/${projectId}/pay-apps/${result.pay_app_id}?step=1`)
      }
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Could not create retainage release')
    } finally {
      setBillRetentionBusy(false)
    }
  }

  const maxLineRetPct = detailMatches
    ? (detail.lines ?? []).reduce((max, l) => Math.max(max, parseFloat(l.retainage_pct) || 0), 0)
    : 0
  const step5RetentionWarn = maxLineRetPct > RETAINAGE_WARN_THRESHOLD
    ? `Retention is set to ${maxLineRetPct}% — is that right?`
    : null
  const retentionResolved = resolveRetentionDefault(
    project?.default_retainage,
    companySettings?.default_retainage,
  )
  const projectDefaultRet = retentionResolved.pct
  const belowDefaultCount = detailMatches
    ? countLinesBelowDefault(detail.lines ?? [], projectDefaultRet)
    : 0
  const showBelowDefaultNotice = belowDefaultCount > 0 && !retNoticeDismissed
  const scheduledSum = detailMatches
    ? (detail.scheduled_sum != null
      ? parseFloat(String(detail.scheduled_sum))
      : (detail.lines ?? []).reduce((s, l) => s + (parseFloat(String(l.scheduled_value)) || 0), 0))
    : null
  const linesInvalid = !gridValid
    || anyLineExceeds100(editableLinesFromDetail(detail?.lines, g703Draft))

  const emailModalContent = (
    <div
      className="panel"
      style={{ ...modalBoxStyle, maxWidth: 480, width: '95%', padding: 0, border: '1px solid var(--line)' }}
      data-testid="payapp-email-modal-content"
      onClick={e => e.stopPropagation()}
    >
      <div className="panel-h">
        <div className="panel-tt">Send Pay App to Owner</div>
      </div>
      <div className="panel-bp">
        <label style={{ display: 'block', marginBottom: 16 }}>
          <span style={lbl}>Owner email</span>
          <input
            type="email"
            className="fld-i"
            style={fld}
            placeholder="owner@example.com"
            value={emailTo}
            onChange={e => setEmailTo(e.target.value)}
          />
        </label>
        {emailModalMessage && (
          <p style={{ fontSize: 12, color: 'var(--green)', marginBottom: 12 }}>{emailModalMessage}</p>
        )}
        {err && <p style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12 }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn b-ol" disabled={busy} onClick={() => setShowEmailModal(false)}>
            Skip
          </button>
          <button
            type="button"
            className="btn b-dk"
            data-testid="payapp-email-modal-send"
            disabled={busy || !emailTo.trim() || !!emailModalMessage || linesInvalid}
            onClick={handleEmailFromModal}
          >
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )

  const panel = (
    <div
      style={embedded ? boxStyle : { ...boxStyle, maxWidth: 720, width: '95%' }}
      data-testid="payapp-editor-modal"
      onClick={embedded ? undefined : e => e.stopPropagation()}
    >
      <div style={embedded
        ? { marginBottom: 16 }
        : { padding: '16px 20px', borderBottom: '1px solid var(--line)' }
      }>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: embedded ? 18 : 16, fontWeight: 700, color: 'var(--ink)' }}
            data-testid={`payapp-editor-step-${step}`}
          >
            Pay application · Step {step} of {TOTAL_STEPS}
            <span
              style={{ fontWeight: 500, color: 'var(--ink3)', marginLeft: 8, fontSize: embedded ? 14 : 13 }}
              data-testid={`payapp-editor-step-label-${step}`}
            >
              {STEP_LABELS[step - 1]}
            </span>
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: embedded ? 13 : 16, cursor: 'pointer', color: 'var(--ink3)' }}>
            {embedded ? '← Back to project' : '✕'}
          </button>
        </div>
        {project && parseFloat(project.change_order_total ?? '0') > 0 && (
          <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 6 }} data-testid="payapp-editor-contract-breakdown">
            <ContractBreakdown
              originalContract={project.original_contract}
              changeOrderTotal={project.change_order_total}
              scheduledSum={scheduledSum}
            />
            {billingProgress && billingProgress.contractTotal > 0 && (
              <span style={{ marginLeft: 8 }}>· {billingProgress.pctBilled}% billed (revised)</span>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: embedded ? '0' : '16px 20px' }}>
        {step === 1 && (
          <form onSubmit={handleStep1}>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={lbl}>Period label</span>
              <input className="fld-i" style={fld} value={periodLabel} onChange={e => setPeriodLabel(e.target.value)} />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <label><span style={lbl}>Period start</span>
                <input type="date" className="fld-i" style={fld} value={periodStart} onChange={e => setPeriodStart(e.target.value)} /></label>
              <label><span style={lbl}>Period end</span>
                <input type="date" className="fld-i" style={fld} value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} /></label>
            </div>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={lbl}>PO number (optional)</span>
              <input className="fld-i" style={fld} value={poNumber} onChange={e => setPoNumber(e.target.value)} />
            </label>
            <div style={{ display: 'block', marginBottom: 16 }}>
              <span style={lbl}>Special notes (optional)</span>
              <RichTextNotes
                value={specialNotes}
                onChange={setSpecialNotes}
                placeholder="Payment terms, ACH info, thank you note..."
              />
            </div>
            {err && <p style={{ color: 'var(--red)', fontSize: 12 }}>{err}</p>}
            <button type="submit" className="btn b-dk" disabled={busy}>{busy ? 'Saving…' : 'Next → Change orders'}</button>
          </form>
        )}

        {step === 2 && activeId != null && (
          <div>
            <ChangeOrdersStep
              ref={changeOrdersRef}
              payAppId={activeId}
              projectId={projectId}
              changeOrders={detail?.change_orders ?? []}
              attachments={detail?.attachments ?? []}
              defaultRetainagePct={projectDefaultRet}
              onRefresh={() => {
                refreshDetail(qc, activeId)
                void qc.refetchQueries({ queryKey: ['pay-app-detail', activeId] })
                void qc.invalidateQueries({ queryKey: ['project', projectId] })
              }}
              onOpenPayApp={(id) => navigate(`/projects/${projectId}/pay-apps/${id}?step=2`)}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button type="button" className="btn b-ol" onClick={() => setStep(1)}>← Back</button>
              <button type="button" className="btn b-dk" onClick={handleStep2Next}>Next → Billing grid</button>
            </div>
          </div>
        )}

        {step === 3 && activeId != null && (
          <div>
          <PayAppG703Grid
            payAppId={activeId}
            embed
            defaultRetainagePct={projectDefaultRet}
            draftLines={g703Draft}
            onDraftLinesChange={setG703Draft}
            onValidityChange={setGridValid}
            highlightRetCells={highlightRetCells}
            retentionHeld={projectRetention}
            onBillRetention={handleBillRetention}
            billRetentionBusy={billRetentionBusy}
            onBack={() => setStep(2)}
            backLabel="← Back to Change orders"
            onSaved={async () => {
              await qc.invalidateQueries({ queryKey: ['pay-app-detail', activeId] })
              await qc.refetchQueries({ queryKey: ['pay-app-detail', activeId] })
              setStep(4)
            }}
          />
          </div>
        )}

        {step === 4 && activeId != null && (
          <div>
            <AttachmentsStep
              payAppId={activeId}
              projectId={projectId}
              attachments={detail?.attachments ?? []}
              changeOrders={detail?.change_orders ?? []}
              onRefresh={() => {
                refreshDetail(qc, activeId)
                void qc.refetchQueries({ queryKey: ['pay-app-detail', activeId] })
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button type="button" className="btn b-ol" onClick={() => setStep(3)}>← Back</button>
              <button
                type="button"
                className="btn b-dk"
                onClick={async () => {
                  await qc.invalidateQueries({ queryKey: ['pay-app-detail', activeId] })
                  await qc.invalidateQueries({ queryKey: ['project-payapps', projectId] })
                  setStep(5)
                }}
              >
                Next → Review & send
              </button>
            </div>
          </div>
        )}

        {step === 5 && activeId != null && (
          <div>
            <div className="panel" style={{ padding: 14, marginBottom: 14, fontSize: 13 }}>
              <div><strong style={{ color: 'var(--ink)' }}>{detail?.period_label ?? periodLabel ?? 'Pay app'}</strong></div>
              <div style={{ color: 'var(--ink3)', marginTop: 6 }}>
                Amount due:{' '}
                <span className="mono-c">
                  {step5AmountDue != null ? fmtMoney(step5AmountDue) : '…'}
                </span>
                {' · '}Retainage held:{' '}
                <span className="mono-c">
                  {step5Retainage != null ? fmtMoney(step5Retainage) : '…'}
                </span>
              </div>
              {showChangeOrderCaption && project && (
                <div style={{ color: 'var(--ink4)', marginTop: 4, fontSize: 12 }} data-testid="payapp-step5-contract">
                  <ContractBreakdown
                    originalContract={project.original_contract}
                    changeOrderTotal={project.change_order_total}
                    scheduledSum={scheduledSum}
                  />
                </div>
              )}
              {showChangeOrderCaption && !project && (
                <div style={{ color: 'var(--ink4)', marginTop: 4, fontSize: 12 }}>
                  Includes change orders: <span className="mono-c">{fmtMoney(changeOrderTotal)}</span>
                </div>
              )}
              {step5RetentionWarn && (
                <div
                  style={{ color: 'var(--amber)', marginTop: 8, fontSize: 12 }}
                  data-testid="payapp-step5-retention-warn"
                >
                  {step5RetentionWarn}
                </div>
              )}
              {showBelowDefaultNotice && (
                <div
                  style={{
                    marginTop: 10,
                    padding: '10px 12px',
                    borderRadius: 6,
                    background: 'var(--amber-l, #fffbeb)',
                    border: '1px solid var(--amber, #eab308)',
                    fontSize: 12,
                    color: 'var(--ink2)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    alignItems: 'center',
                  }}
                  data-testid="payapp-step5-below-default-notice"
                >
                  <span>
                    {retentionNoticeMessage(belowDefaultCount, projectDefaultRet, retentionResolved.source)}
                    {' '}
                    <button
                      type="button"
                      className="btn btn-sm b-ol"
                      style={{ padding: '2px 8px', fontSize: 11, verticalAlign: 'baseline' }}
                      data-testid="payapp-step5-review-lines"
                      onClick={() => {
                        setHighlightRetCells(true)
                        setStep(3)
                      }}
                    >
                      review lines
                    </button>
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm b-ol"
                    style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 11 }}
                    data-testid="payapp-step5-keep-entered"
                    onClick={async () => {
                      if (activeId != null) await dismissPayAppRetentionNotice(activeId)
                      setRetNoticeDismissed(true)
                      await qc.invalidateQueries({ queryKey: ['pay-app-detail', activeId] })
                    }}
                  >
                    Keep as entered
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm b-dk"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    data-testid="payapp-step5-apply-default-ret"
                    onClick={async () => {
                      if (activeId == null || !detail?.lines) return
                      const draftMap = new Map((g703Draft ?? []).map((d) => [d.id, d]))
                      const edited = applyDefaultRetainageToBelow(
                        detail.lines.map((l) => toEditableLine(l, draftMap.get(l.id))),
                        projectDefaultRet,
                      )
                      await updatePayAppLines(activeId, { lines: buildLinesSavePayload(edited) })
                      await dismissPayAppRetentionNotice(activeId)
                      clearG703Draft(activeId)
                      setG703Draft(undefined)
                      setRetNoticeDismissed(true)
                      setHighlightRetCells(true)
                      await qc.invalidateQueries({ queryKey: ['pay-app-detail', activeId] })
                      setStep(3)
                    }}
                  >
                    Apply {projectDefaultRet}% to these lines
                  </button>
                </div>
              )}
              {!isSubmitted && (
                <p style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 8 }} data-testid="payapp-draft-waiver-note">
                  Conditional lien waiver will be generated at submission.
                </p>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              <button type="button" className="btn b-ol" data-testid="payapp-pdf-btn"
                onClick={() => window.open(getPayAppPdfUrl(activeId, getToken() ?? ''), '_blank')}>
                Download PDF
              </button>
              <button
                type="button"
                className="btn b-dk"
                data-testid="payapp-submit-btn"
                disabled={busy || isSubmitted || linesInvalid}
                onClick={handleSubmit}
              >
                {busy ? 'Submitting…' : isSubmitted ? 'Submitted ✓' : 'Submit Pay App'}
              </button>
            </div>
            {waiverGenerated && (
              <>
                <p style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 8 }}>
                  ✓ Conditional lien waiver generated and saved to project Lien tab.
                </p>
                <button
                  type="button"
                  onClick={() => { onSaved(); onClose(); }}
                  style={{ marginTop: 12, fontSize: 13 }}
                  className="btn-primary"
                >
                  ← Back to project
                </button>
              </>
            )}
            <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14 }}>
              <span style={lbl}>Send by email</span>
              {firstOverbilledLine(editableLinesFromDetail(detail?.lines, g703Draft)) && (
                <p style={{ fontSize: 12, color: 'var(--red)', margin: '6px 0' }} data-testid="payapp-send-overbill">
                  {overbillLineMessage(firstOverbilledLine(editableLinesFromDetail(detail?.lines, g703Draft))!)}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="email" className="fld-i" style={{ ...fld, flex: 1 }} placeholder="owner@example.com"
                  value={emailTo} onChange={e => setEmailTo(e.target.value)} />
                <button
                  type="button"
                  className="btn b-ol"
                  data-testid="payapp-send-btn"
                  disabled={busy || !emailTo.trim() || linesInvalid}
                  onClick={handleEmail}
                >
                  Send
                </button>
              </div>
              {emailSent && <p style={{ fontSize: 12, color: 'var(--green)', marginTop: 8 }} data-testid="payapp-email-sent">Sent</p>}
            </div>
            {err && <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 10 }}>{err}</p>}
            <button type="button" className="btn b-ol" style={{ marginTop: 14 }} onClick={() => setStep(4)}>
              ← Back to attachments
            </button>
          </div>
        )}
      </div>
    </div>
  )

  if (embedded) {
    return (
      <>
        {panel}
        {showEmailModal && (
          <ModalPortal testId="payapp-email-modal" onBackdropClick={() => setShowEmailModal(false)}>
            {emailModalContent}
          </ModalPortal>
        )}
      </>
    )
  }

  return (
    <>
      <ModalPortal testId="payapp-editor-backdrop" onBackdropClick={onClose}>
        {panel}
      </ModalPortal>
      {showEmailModal && (
        <ModalPortal testId="payapp-email-modal" onBackdropClick={() => setShowEmailModal(false)}>
          {emailModalContent}
        </ModalPortal>
      )}
    </>
  )
}
